import React, { useState } from 'react';
import { DialogActions, Box } from '@mui/material';
import { useForm, FormProvider, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { usersServices } from '@services';
import { FieldV2, Label } from 'components/common/form-controls';
import { Switch, ButtonV2 } from 'components/common/ui';
import { LANGUAGES, SUBSCRIBER_ROLES } from '@constants';

import styles from './AddDriverModal.style';
import validationSchema from './addDriversValidationSchema';

const AddEditDriversForm = ({
  onSubmit,
  onCancel,
  onRemove,
  id,
  subscriptionId,
  defaultValues = {
    driversLicenseIssueDate: '',
    dateOfBirth: '',
    activeDriver: false,
  },
}) => {
  const [userExists, setUserExists] = useState(!!id);

  const resolver = yupResolver(validationSchema);
  const methods = useForm({
    resolver,
    mode: 'onChange',
    defaultValues,
  });
  const { handleSubmit, formState, getValues, reset } = methods;
  const role = methods.watch('role');
  const roles = Object.keys(SUBSCRIBER_ROLES);
  const isDriver = role === SUBSCRIBER_ROLES.driver.value;

  const activeDriver = methods.watch('activeDriver');
  const rolesOptions = roles.map((key) => SUBSCRIBER_ROLES[key]);

  const handleEmailBlur = async () => {
    if (userExists) {
      return;
    }
    try {
      const { email } = getValues();

      if (email) {
        setUserExists(false);

        const { data } = await usersServices.getUserByEmail(email);
        const currentSubscription = data?.subscriptions?.find((item) => {
          return item?.id === subscriptionId;
        });

        const subscriberRole = Object.keys(SUBSCRIBER_ROLES).find(
          (key) => SUBSCRIBER_ROLES[key].value === currentSubscription?.role
        );

        if (data) {
          reset({
            email,
            role: SUBSCRIBER_ROLES[subscriberRole]?.value || '',
            firstName: data.firstName,
            lastName: data.lastName,
            phoneNumber: data.phoneNumber,
            dateOfBirth: data.dateOfBirth,
            driversLicenseIssueDate: data.driversLicenseIssueDate,
            driversLicenseNumber: data.driversLicenseNumber,
            lang: data.lang,
            activeDriver: data.activeDriver || false,
          });
          setUserExists(true);
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn(e);
      setUserExists(false);
    }
  };

  const handleChangeRole = (value) => {
    methods.setValue('role', value);
    const driver = value === SUBSCRIBER_ROLES.driver.value;
    const isAdmin = value === SUBSCRIBER_ROLES.admin.value;
    const isOwner = value === SUBSCRIBER_ROLES.owner.value;

    if (driver || isOwner) {
      methods.setValue('activeDriver', true);
    }
    if (isAdmin) {
      methods.setValue('activeDriver', false);
    }
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={styles.fieldRow}>
          <FieldV2
            isRequired
            name="email"
            label="Email"
            disabled={userExists}
            control={methods.control}
            onBlur={handleEmailBlur}
          />
          <FieldV2
            isRequired
            name="phoneNumber"
            label="Phone Number"
            disabled={userExists}
            control={methods.control}
          />
        </Box>
        <Box sx={styles.fieldRow}>
          <FieldV2
            isRequired
            name="firstName"
            label="First Name"
            disabled={userExists}
            control={methods.control}
          />
          <FieldV2
            isRequired
            name="lastName"
            label="Last Name"
            disabled={userExists}
            control={methods.control}
          />
        </Box>
        <Box sx={styles.fieldRow}>
          <FieldV2
            isRequired
            name="role"
            label="Role"
            type="dropdown"
            options={rolesOptions}
            control={methods.control}
            onChange={handleChangeRole}
          />
          <Controller
            name="activeDriver"
            control={methods.control}
            render={({ field: { onChange, value = '' } }) => (
              <Box
                sx={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                }}
              >
                <Label name="activeDriver">Active Driver</Label>
                <Switch
                  onChange={onChange}
                  value={value}
                  disabled={!role || isDriver}
                />
              </Box>
            )}
          />
        </Box>
        <Box sx={styles.fieldRow}>
          <FieldV2
            isRequired
            name="lang"
            label="Language"
            type="dropdown"
            options={LANGUAGES}
            control={methods.control}
            disabled={userExists && !!defaultValues.language}
          />
          <FieldV2
            disableFuture
            isRequired={activeDriver}
            type="date"
            name="dateOfBirth"
            label="Date of Birth"
            control={methods.control}
            disabled={userExists && !!defaultValues.dateOfBirth}
          />
        </Box>
        <Box sx={styles.fieldRow}>
          <FieldV2
            control={methods.control}
            isRequired={activeDriver}
            name="driversLicenseNumber"
            label="Driving License"
            disabled={userExists && !!defaultValues.driversLicenseNumber}
          />
          <FieldV2
            disableFuture
            control={methods.control}
            isRequired={activeDriver}
            type="date"
            name="driversLicenseIssueDate"
            label="Issuance Date"
            disabled={userExists && !!defaultValues.driversLicenseIssueDate}
          />
        </Box>

        <DialogActions>
          <Box sx={styles.footer}>
            {id && (
              <ButtonV2 variant="secondary" onClick={onRemove}>
                Remove
              </ButtonV2>
            )}
            <Box sx={styles.buttons}>
              <ButtonV2 variant="secondary" onClick={onCancel}>
                Cancel
              </ButtonV2>
              <ButtonV2 type="submit" loading={formState.isSubmitting}>
                OK
              </ButtonV2>
            </Box>
          </Box>
        </DialogActions>
      </form>
    </FormProvider>
  );
};

export default AddEditDriversForm;
